import React, { useCallback, useEffect, useState } from 'react';

import { Group, Stack, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconCircleCheckFilled, IconCircleOff, IconLock } from '@tabler/icons-react';
import InvoiceActionsMenu, { InvoiceMenuAction } from 'components/invoices/InvoiceActionsMenu';
import InvoiceDrawer, { InvoiceDrawerState } from 'components/invoices/InvoiceDrawer';
import InvoiceStatusBadge from 'components/invoices/InvoiceStatusBadge';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useInvoices from 'hooks/invoice/useInvoices';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CellProps } from 'react-table';
import { AthleteAutoComplete, Invoice } from 'types';
import { formatDateForApi, parseDateTimeToDate } from 'utils/date-functions';
import { getPaymentMethodName } from 'utils/formatters';

import InvoiceCollectModal from './InvoiceCollectModal';
import InvoiceRefundModal from './InvoiceRefundModal';
import InvoiceReopenModal from './InvoiceReopenModal';
import InvoiceSendModal from './InvoiceSendModal';
import InvoiceVoidModal from './InvoiceVoidModal';
import { INVOICE_ACTION_TO_READABLE_TITLE_MAP } from './utils';

export interface InvoicesTableSearchFilters {
  search?: string;
  dueDateRange?: [Date | null, Date | null];
  paymentDateRange?: [Date | null, Date | null];
  status?: string[];
  productType?: string | null;
  billingSources?: string[];
  autoPay?: string | null;
  athlete?: string;
  athletePackage?: string;
  /** Payment method categories — see PAYMENT_METHOD_CATEGORIES for the tokens. */
  paymentMethods?: string[];
}

const InvoicesTable = ({
  setOrderingValue,
  setFirstInvoicePrice,
  searchFilters,
  enableHook,
  removeColumns = [],
  permission = true,
  margin = 12,
  bgc = 'white',
  initialInvoiceId,
  onDrawerClose,
}: {
  searchFilters: InvoicesTableSearchFilters;
  setOrderingValue?: React.Dispatch<React.SetStateAction<string>>;
  setFirstInvoicePrice?: React.Dispatch<React.SetStateAction<number>>;
  removeColumns?: string[];
  enableHook?: boolean;
  permission?: boolean;
  margin?: number;
  bgc?: 'white' | 'gray';
  initialInvoiceId?: string;
  onDrawerClose?: () => void;
}): JSX.Element => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatCurrency, copy, dateFormat } = useLocalization();

  const [tableData, setTableData] = useState<Invoice[]>([]);
  const [ordering, setOrdering] = useState('dueDate');
  const [ascending, setAscending] = useState(true);

  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [invoiceDrawerState, setInvoiceDrawerState] = useState<InvoiceDrawerState>('View');
  const [isInvoiceDrawerOpen, { open: openInvoiceDrawer, close: closeInvoiceDrawer }] = useDisclosure(false);
  const [isCollectModalOpen, { open: openCollectModal, close: closeCollectModal }] = useDisclosure(false);
  const [isRefundModalOpen, { open: openRefundModal, close: closeRefundModal }] = useDisclosure(false);
  const [isVoidModalOpen, { open: openVoidModal, close: closeVoidModal }] = useDisclosure(false);
  const [isReopenModalOpen, { open: openReopenModal, close: closeReopenModal }] = useDisclosure(false);
  const [isSendModalOpen, { open: openSendModal, close: closeSendModal }] = useDisclosure(false);

  const { hasPerm, session } = useAuth();
  const invoiceMutatePerm = hasPerm('InvoiceMutate');

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useInvoices({
      ...searchFilters,
      status: !!searchFilters?.status?.length ? searchFilters.status : null,
      dueDateStart: formatDateForApi(searchFilters.dueDateRange?.[0]),
      dueDateEnd: formatDateForApi(searchFilters.dueDateRange?.[1]),
      paymentDateStart: formatDateForApi(searchFilters.paymentDateRange?.[0]),
      paymentDateEnd: formatDateForApi(searchFilters.paymentDateRange?.[1]),
      ordering: ascending ? ordering : `-${ordering}`,
      enabled: enableHook,
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue?.(!a ? header : `-${header}`);
      return !a;
    });
  };

  const handleCloseAction = (): void => {
    closeCollectModal();
    closeRefundModal();
    closeVoidModal();
    closeReopenModal();
    closeSendModal();
    closeInvoiceDrawer();
    onDrawerClose?.();

    // Clear collect modal URL params
    const params = new URLSearchParams(searchParams);
    params.delete('collectModal');
    params.delete('verb');
    setSearchParams(params, { replace: true });
  };

  const handleAction = (invoiceId: string, action: InvoiceMenuAction): void => {
    setSelectedInvoice(invoiceId);
    if (action === 'View') {
      setInvoiceDrawerState('View');
      openInvoiceDrawer();
    } else if (action === 'Edit') {
      // Staff correcting a batch of dates work from the list, so the entry point they reach first
      // opens the same mode the drawer's own Edit button does.
      const invoice = tableData.find(i => i.invoiceId === invoiceId);
      setInvoiceDrawerState(invoice?.isPaymentDateEditable ? 'Edit Payment Date' : 'Edit Invoice');
      openInvoiceDrawer();
    } else if (action === 'Collect') {
      openCollectModal();
      // Set URL params for collect modal
      const params = new URLSearchParams(searchParams);
      params.set('collectModal', invoiceId);
      params.set('verb', 'collect');
      setSearchParams(params);
    } else if (action === 'Refund') {
      openRefundModal();
    } else if (action === 'Void') {
      openVoidModal();
    } else if (action === 'Reopen') {
      openReopenModal();
    } else if (action === 'Send') {
      openSendModal();
    }
  };

  const handleNameClick = useCallback(
    (athlete: AthleteAutoComplete): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!isLoading && data) {
      const formattedData = data.pages.map(page => page.data.results).flat();
      setTableData(formattedData);

      const firstInvoicePretaxTotal = formattedData[0]?.pretaxTotal;
      if (firstInvoicePretaxTotal) {
        setFirstInvoicePrice?.(firstInvoicePretaxTotal);
      }
    }
  }, [isLoading, data, setTableData, setFirstInvoicePrice]);

  useEffect(() => {
    if (initialInvoiceId && !isInvoiceDrawerOpen) {
      setSelectedInvoice(initialInvoiceId);
      setInvoiceDrawerState('View');
      openInvoiceDrawer();
    }
  }, [initialInvoiceId, isInvoiceDrawerOpen, openInvoiceDrawer]);

  // Restore collect modal from URL params
  useEffect(() => {
    const collectModalId = searchParams.get('collectModal');
    if (collectModalId && !isCollectModalOpen) {
      setSelectedInvoice(collectModalId);
      openCollectModal();
    }
  }, [searchParams, isCollectModalOpen, openCollectModal]);

  const columns = [
    {
      id: 'dueDate',
      accessor: 'dueDate' as const,
      Header: 'Due Date',
      Cell: ({ value }: CellProps<Invoice>) =>
        dayjs(parseDateTimeToDate(value, session?.gym?.timezone)).format(dateFormat),
    },
    {
      id: 'status',
      Header: 'Status',
      Cell: ({ row }: CellProps<Invoice>) =>
        ['PAYMENT FAILED', 'FAILED PAST DUE'].includes(row.original.status) ? (
          <Tooltip
            label={`${row.original.failureCode} : ${row.original.failureMessage}`}
            multiline
            withinPortal
            maw={'500px'}
            events={{ hover: true, focus: false, touch: true }}
          >
            <div>
              <InvoiceStatusBadge status={row.original.status} />
            </div>
          </Tooltip>
        ) : (
          <InvoiceStatusBadge status={row.original.status} />
        ),
    },
    {
      id: 'lastAction',
      Header: 'Last Action',
      Cell: ({ row }: CellProps<Invoice>) =>
        row.original.lastAction && (
          <Text truncate>{INVOICE_ACTION_TO_READABLE_TITLE_MAP[row.original.lastAction]}</Text>
        ),
      maxWidth: '130px',
    },
    {
      id: 'pretaxTotal',
      accessor: 'pretaxTotal' as const,
      Header: copy.financial.pretax,
      Cell: ({ value }: CellProps<Invoice>) => formatCurrency(value / 100),
      minWidth: '80px',
    },
    {
      id: 'discount',
      Header: 'Discount',
      Cell: ({ row }: CellProps<Invoice>) => {
        const total = row.original.invoiceItems.reduce((sum, item) => sum + (item.discountAmount ?? 0), 0);
        if (total <= 0) return <Text color='dimmed'>—</Text>;
        return <Text>{formatCurrency(total / 100)}</Text>;
      },
      minWidth: '80px',
    },
    {
      id: 'tax',
      accessor: 'tax' as const,
      Header: copy.financial.tax,
      Cell: ({ value }: CellProps<Invoice>) => formatCurrency(value / 100),
      minWidth: '80px',
    },
    {
      id: 'total',
      accessor: 'total' as const,
      Header: 'Total',
      Cell: ({ value }: CellProps<Invoice>) => formatCurrency(value / 100),
      minWidth: '80px',
    },
    {
      id: 'paidAmount',
      accessor: 'paidAmount' as const,
      Header: 'Paid',
      Cell: ({ value }: CellProps<Invoice>) => formatCurrency(value / 100),
      minWidth: '80px',
    },
    {
      id: 'athlete',
      accessor: 'athlete.fullName' as const,
      Header: 'Athlete',
      Cell: ({ row }: CellProps<Invoice>) => (
        <Group noWrap spacing='xs'>
          <Text
            underline
            sx={{ cursor: 'pointer' }}
            onClick={() => handleNameClick(row.original.athlete)}
            truncate
          >
            {row.original.athlete.fullName}
          </Text>
        </Group>
      ),
      maxWidth: '180px',
    },
    {
      id: 'billingSource',
      accessor: 'invoiceItems' as const,
      Header: 'Billing Source',
      Cell: ({ row }: CellProps<Invoice>) => (
        <Stack spacing={0}>
          {row.original.invoiceItems.map(invoiceItem => (
            <Text key={invoiceItem.invoiceItemId} size='sm' truncate>
              {invoiceItem.quantity === 1
                ? invoiceItem.productName
                : `(${invoiceItem.quantity}) ${invoiceItem.productName}`}
            </Text>
          ))}
        </Stack>
      ),
      maxWidth: '200px',
    },
    {
      id: 'paymentDate',
      accessor: 'paymentDate' as const,
      Header: 'Payment Date',
      Cell: ({ value }: CellProps<Invoice>) => (value ? dayjs(value).format(dateFormat) : ''),
    },
    {
      id: 'autoPay',
      accessor: 'autoPay' as const,
      Header: 'Auto Pay',
      Cell: ({ value }: CellProps<Invoice>) =>
        value ? (
          <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff size={theme.fontSizes.md} color='gray' />
        ),
    },
    {
      id: 'paymentMethod',
      accessor: 'paymentMethod' as const,
      Header: 'Payment Method',
      Cell: ({ row }: CellProps<Invoice>) => {
        if (row.original.paymentMethod) {
          return (
            <Group noWrap>
              {row.original.paymentMethod.isLocked && (
                <Tooltip
                  label={row.original.paymentMethod.failureMessage}
                  disabled={!row.original.paymentMethod.failureMessage}
                >
                  <IconLock color='red' size='10px' />
                </Tooltip>
              )}
              <Text size='sm' truncate>
                {getPaymentMethodName(row.original.paymentMethod)}
              </Text>
            </Group>
          );
        }
        return <Text size='sm'>{row.original.nonStripePaymentMethod}</Text>;
      },
      maxWidth: '100px',
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<Invoice>) => (
        <Group position='right' px='xs'>
          <InvoiceActionsMenu
            disabled={!permission || !invoiceMutatePerm}
            row={row}
            handleAction={handleAction}
          />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns.filter(c => !removeColumns.includes(c.id))}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={
          isDesktop ? ['dueDate', 'status', 'total', 'tax', 'updatedAt', 'paymentDate', 'athlete'] : []
        }
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering={ordering}
        margin={margin}
        bgc={bgc}
      />
      {/* Staff-only: this table is rendered from the owner and ownerMobile pages alone. The
          athlete's own Invoice Detail page reaches the same modal directly and leaves the prop
          unset, which is what keeps the auto-pay toggle off that surface. */}
      <InvoiceCollectModal
        invoiceId={selectedInvoice}
        opened={isCollectModalOpen}
        close={handleCloseAction}
        isStaffContext
      />
      <InvoiceRefundModal invoiceId={selectedInvoice} opened={isRefundModalOpen} close={handleCloseAction} />
      <InvoiceVoidModal invoiceId={selectedInvoice} opened={isVoidModalOpen} close={handleCloseAction} />
      <InvoiceReopenModal invoiceId={selectedInvoice} opened={isReopenModalOpen} close={handleCloseAction} />
      <InvoiceSendModal invoiceId={selectedInvoice} opened={isSendModalOpen} close={handleCloseAction} />
      <InvoiceDrawer
        invoiceId={selectedInvoice}
        state={invoiceDrawerState}
        setState={setInvoiceDrawerState}
        opened={isInvoiceDrawerOpen}
        close={handleCloseAction}
      />
    </>
  );
};

export default InvoicesTable;
